import { useEffect, useState, useRef, memo } from 'react';
import { useDispatch } from 'react-redux';
import { Range, getTrackBackground } from 'react-range';
import FilterItem from '../FilterItem';
import { updateBudgetFilter } from '../mainFilterSlice';
import { scrollToResults, shallowEqual } from '../../../utilities/helpers';
import translate, { t } from '../../../utilities/translator';
import { pushEvent } from '../../../utilities/analytics';
import { BUDGET_FILTER_UPDATE_EVENT } from '../../../constants/analyticEvents';
import { clearSelectedHotel } from '../../search/searchSlice';
import { filterClearClick, setLastChangedFilter } from '../../../appSlice';
import useHandleMainFilterState from '../../../hooks/mainFilter/useHandleMainFilterState';
import { getDisplayPrice } from '../../../utilities/priceHelper';
import { selectFilterData } from '../selectors';
import { useAppSelector } from '../../../hooks/hooks';

const getFilterLabel = (values: number[]) => {
  if (values.length)
    return (
      <span className="flex gap-1 items-center">
        {getDisplayPrice(values[0])} - {values[1] >= 3000 ? t('mainFilter.any_budget') : getDisplayPrice(values[1])}
      </span>
    );

  return translate('holidayFinder.mainFilter.any_budget');
};

const Budget = () => {
  const clearTimerRef = useRef(null);
  const saveTimerRef = useRef(null);
  const dispatch = useDispatch();
  const {
    searchEngine: { budget },
  } = useAppSelector(selectFilterData);
  const { initial, min, max } = budget;

  const [isClean, setIsClean] = useState(true);
  const [values, setValues] = useState([min, max]);
  const [filterLabel, setFilterLabel] = useState(getFilterLabel(values));
  const showClearButton = !shallowEqual(initial, { min, max });
  const isRtl = document.querySelector('html')?.getAttribute('dir') === 'rtl';

  useEffect(() => {
    const [min, max] = values;
    setIsClean(min === budget.min && max === budget.max);
  }, [budget, values]);

  useEffect(() => {
    setValues([min, max]);
    setFilterLabel(getFilterLabel([min, max]));
  }, [budget]);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current !== null) {
        clearTimeout(clearTimerRef.current);
      }
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const onFinalChange = (value) => {};

  const [isOpen, setIsOpen] = useHandleMainFilterState({ filter: 'budget' });

  const isStateEqual = min === values[0] && max === values[1];

  const handleSaveClick = () => {
    if (isStateEqual) return undefined;

    setFilterLabel(getFilterLabel(values));
    saveTimerRef.current = setTimeout(() => {
      dispatch(updateBudgetFilter(values));
      dispatch(clearSelectedHotel());
      dispatch(setLastChangedFilter('budget'));
    }, 0);
    pushEvent(BUDGET_FILTER_UPDATE_EVENT, values);
    setIsOpen(false);
    scrollToResults();
  };

  const handleClearClick = () => {
    setValues([min, max]);
    if (shallowEqual(initial, { min, max })) return undefined;
    dispatch(filterClearClick());
    clearTimerRef.current = setTimeout(() => {
      dispatch(updateBudgetFilter([initial.min, initial.max]));
    }, 0);
    scrollToResults();
  };

  return (
    <FilterItem
      label={translate('holidayFinder.mainFilter.your_budget')}
      eventName="budget"
      value={filterLabel}
      onSave={handleSaveClick}
      onClear={handleClearClick}
      className="pull-right"
      icon="ri-wallet-3-line"
      showClearButton={showClearButton}
      clean={isClean}
      resetOnClickOutside={() => setValues([min, max])}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <div className="budget-filter__wrap budget-filter">
        <div className="budget-filter__title">{translate('holidayFinder.mainFilter.total_price')}</div>
        <Range
          step={5}
          rtl={isRtl}
          min={initial.min}
          max={initial.max}
          values={values}
          onChange={setValues}
          onFinalChange={onFinalChange}
          renderTrack={({ props, children }) => (
            <div className="track-wrap">
              <div
                {...props}
                className="track-line"
                style={{
                  ...props.style,
                  background: getTrackBackground({
                    values,
                    colors: ['#D6D6D7', '#73777C', '#D6D6D7'],
                    min: initial.min,
                    max: initial.max,
                    rtl: isRtl,
                  }),
                }}
              >
                {children}
              </div>
            </div>
          )}
          renderThumb={({ props }) => (
            <div {...props} style={{ ...props.style }} className="slider-thumb">
              <div className="slider-line" />
              <div className="slider-line" />
              <div className="slider-line" />
            </div>
          )}
        />
        <div className="budget-filter__range-values">
          <div className="budget-filter__range-min">
            <div className="budget-filter__label">{translate('holidayFinder.mainFilter.min')}</div>
            <div className="budget-filter__value">{getDisplayPrice(values[0])}</div>
          </div>
          <div className="budget-filter__range-max">
            <div className="budget-filter__label">{translate('holidayFinder.mainFilter.max')}</div>
            <div className="budget-filter__value">{values[1] >= 3000 ? t('mainFilter.any') : getDisplayPrice(values[1])}</div>
          </div>
        </div>
      </div>
    </FilterItem>
  );
};

export default memo(Budget);
